/**
 * Операции над личным пространством.
 *
 * Все изменения возвращают новое значение: пространство иммутабельно, поэтому
 * состояние экрана и сохранённые данные не могут разъехаться незаметно.
 */

import type {
  Baseline,
  EntitlementSnapshot,
  KeyboardProfile,
  LanguageCode,
  MasteryStat,
  MasteryUnit,
  TypingMetrics,
  XpTransaction,
} from '@type2x/shared-contracts';
import {
  DEFAULT_LAYOUT_BY_LANGUAGE,
  ENTITLEMENT_CACHE_TTL_MS,
  emptyStat,
} from '@type2x/shared-contracts';

import type { DayActivity, UnlockedAchievement } from '../contracts/achievements.js';
import { ACTIVITY_LOG_DAYS } from '../contracts/achievements.js';
import type {
  Appearance,
  LanguageProfile,
  PrivacySettings,
  StartPath,
  Workspace,
  WorkspaceOwner,
} from '../contracts/workspace.js';
import { DEFAULT_APPEARANCE, WORKSPACE_SCHEMA_VERSION } from '../contracts/workspace.js';
import { awardAchievements } from './achievements.js';

/** Приватность по умолчанию: ничего не публикуется без явного согласия. */
export const DEFAULT_PRIVACY: PrivacySettings = Object.freeze({
  leaderboardOptIn: false,
  displayName: null,
  analyticsConsent: false,
});

/**
 * Путь по умолчанию.
 *
 * Диагностика: она не требует объяснений и подходит тому, кто уже как-то
 * печатает. «Основы» выбираются явно.
 */
export const DEFAULT_START_PATH: StartPath = 'diagnostics';

/** Создаёт пустой профиль языка. */
export function createLanguageProfile(
  language: LanguageCode,
  keyboard?: KeyboardProfile,
  startPath: StartPath = DEFAULT_START_PATH,
): LanguageProfile {
  return {
    language,
    keyboard: keyboard ?? {
      layoutId: DEFAULT_LAYOUT_BY_LANGUAGE[language],
      platform: 'windows',
      form: 'ansi',
    },
    baseline: null,
    units: {},
    stats: {},
    seenInControl: [],
    bestVerifiedWpm: 0,
    bestCpm: 0,
    bestAccuracy: 0,
    typedCharacters: 0,
    practiceDays: [],
    startPath,
    tempoTargetCpm: null,
  };
}

/** Профиль языка в том виде, в каком он мог быть записан прежней версией. */
type StoredProfile = Omit<
  LanguageProfile,
  'startPath' | 'tempoTargetCpm' | 'bestCpm' | 'bestAccuracy' | 'typedCharacters'
> & {
  readonly startPath?: StartPath;
  readonly tempoTargetCpm?: number | null;
  readonly bestCpm?: number;
  readonly bestAccuracy?: number;
  readonly typedCharacters?: number;
};

/** Пространство в том виде, в каком оно прочитано из хранилища. */
export type StoredWorkspace = Omit<
  Workspace,
  'profiles' | 'appearance' | 'achievements' | 'activityLog'
> & {
  /** Достижения появились в пятой версии схемы. */
  readonly achievements?: readonly UnlockedAchievement[];
  /** Журнал занятий по дням появился в шестой версии схемы. */
  readonly activityLog?: readonly DayActivity[];
  readonly profiles: Readonly<Record<string, StoredProfile>>;
  /**
   * Настройки внешнего вида прежней версии: часть полей могла ещё не
   * существовать, поэтому дополняется по одному, а не целиком.
   */
  readonly appearance?: Partial<Appearance>;
};

/**
 * Дополняет пространство, прочитанное из хранилища прежней версии.
 *
 * Поле, добавленное новой версией схемы, в старой записи отсутствует. Молча
 * читать её как есть нельзя: код рассчитывает на поле и получил бы
 * `undefined` там, где по типу стоит значение. Прогресс при этом не теряется —
 * подставляется значение по умолчанию.
 */
export function migrateWorkspace(workspace: StoredWorkspace, now: string): Workspace {
  const profiles: Record<string, LanguageProfile> = {};
  for (const [language, profile] of Object.entries(workspace.profiles)) {
    profiles[language] = {
      ...profile,
      startPath: profile.startPath ?? DEFAULT_START_PATH,
      tempoTargetCpm: profile.tempoTargetCpm ?? null,
      // Рекорды прежних записей не выдумываются: набранное до появления
      // счётчика не восстановить, и приписывать его нельзя.
      bestCpm: profile.bestCpm ?? 0,
      bestAccuracy: profile.bestAccuracy ?? 0,
      typedCharacters: profile.typedCharacters ?? 0,
    };
  }

  const appearance: Appearance = { ...DEFAULT_APPEARANCE, ...workspace.appearance };
  const achievements = workspace.achievements ?? [];
  // Журнал не восстанавливается задним числом: чего не записывали, того нет.
  const activityLog = workspace.activityLog ?? [];

  if (workspace.schemaVersion === WORKSPACE_SCHEMA_VERSION) {
    return { ...workspace, profiles, appearance, achievements, activityLog };
  }

  return {
    ...workspace,
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    profiles,
    appearance,
    achievements,
    activityLog,
    updatedAt: now,
  };
}

/** Меняет настройки внешнего вида пространства. */
export function updateAppearance(
  workspace: Workspace,
  patch: Partial<Appearance>,
  now: string,
): Workspace {
  return {
    ...workspace,
    appearance: { ...workspace.appearance, ...patch },
    updatedAt: now,
  };
}

/** Запоминает достигнутую ступень темпа для активного языка. */
export function setTempoTarget(workspace: Workspace, targetCpm: number, now: string): Workspace {
  return withProfile(workspace, { ...activeProfile(workspace), tempoTargetCpm: targetCpm }, now);
}

/** Задаёт путь начала обучения активному языку. */
export function setStartPath(workspace: Workspace, startPath: StartPath, now: string): Workspace {
  return withProfile(workspace, { ...activeProfile(workspace), startPath }, now);
}

/** Создаёт новое пространство гостя. */
export function createWorkspace(
  owner: WorkspaceOwner,
  uiLanguage: LanguageCode,
  now: string,
): Workspace {
  return {
    owner,
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    uiLanguage,
    activeLanguage: uiLanguage,
    profiles: { [uiLanguage]: createLanguageProfile(uiLanguage) },
    xpLedger: [],
    privacy: DEFAULT_PRIVACY,
    appearance: DEFAULT_APPEARANCE,
    achievements: [],
    activityLog: [],
    entitlements: null,
    createdAt: now,
    updatedAt: now,
  };
}

/** Профиль активного языка. Создаётся на лету, если его ещё нет. */
export function activeProfile(workspace: Workspace): LanguageProfile {
  return (
    workspace.profiles[workspace.activeLanguage] ?? createLanguageProfile(workspace.activeLanguage)
  );
}

function withProfile(workspace: Workspace, profile: LanguageProfile, now: string): Workspace {
  return {
    ...workspace,
    profiles: { ...workspace.profiles, [profile.language]: profile },
    updatedAt: now,
  };
}

/** Переключает активный язык, создавая профиль при необходимости. */
export function switchLanguage(
  workspace: Workspace,
  language: LanguageCode,
  now: string,
): Workspace {
  const profile = workspace.profiles[language] ?? createLanguageProfile(language);
  return {
    ...workspace,
    activeLanguage: language,
    profiles: { ...workspace.profiles, [language]: profile },
    updatedAt: now,
  };
}

/** Задаёт клавиатуру активного языка. */
export function setKeyboard(
  workspace: Workspace,
  keyboard: KeyboardProfile,
  now: string,
): Workspace {
  return withProfile(workspace, { ...activeProfile(workspace), keyboard }, now);
}

/**
 * Фиксирует baseline активного языка.
 *
 * Baseline записывается один раз: повторное измерение не должно обнулять
 * показанный пользователю рост (ТЗ FR-GM-02).
 */
export function setBaseline(workspace: Workspace, baseline: Baseline, now: string): Workspace {
  const profile = activeProfile(workspace);
  if (profile.baseline !== null) return workspace;
  return withProfile(workspace, { ...profile, baseline }, now);
}

/** Добавляет или обновляет учебные единицы и их статистику. */
export function upsertMastery(
  workspace: Workspace,
  units: readonly MasteryUnit[],
  stats: readonly MasteryStat[],
  now: string,
): Workspace {
  const profile = activeProfile(workspace);

  const nextUnits = { ...profile.units };
  for (const unit of units) nextUnits[unit.id] = unit;

  const nextStats = { ...profile.stats };
  for (const stat of stats) nextStats[stat.unitId] = stat;

  return withProfile(workspace, { ...profile, units: nextUnits, stats: nextStats }, now);
}

/** Возвращает статистику единицы, создавая пустую при первом обращении. */
export function statFor(profile: LanguageProfile, unit: MasteryUnit, now: string): MasteryStat {
  return (
    profile.stats[unit.id] ?? emptyStat(unit.id, profile.language, profile.keyboard.layoutId, now)
  );
}

/** Добавляет XP-транзакцию в журнал. */
export function appendXp(workspace: Workspace, transaction: XpTransaction, now: string): Workspace {
  if (transaction.amount <= 0) return workspace;
  return { ...workspace, xpLedger: [...workspace.xpLedger, transaction], updatedAt: now };
}

/** Суммарный XP пространства. */
export function totalXp(workspace: Workspace): number {
  return workspace.xpLedger.reduce((sum, transaction) => sum + transaction.amount, 0);
}

/** Отмечает день практики активного языка. */
export function recordPracticeDay(workspace: Workspace, isoDate: string, now: string): Workspace {
  const profile = activeProfile(workspace);
  const day = isoDate.slice(0, 10);
  if (profile.practiceDays.includes(day)) return workspace;
  return withProfile(workspace, { ...profile, practiceDays: [...profile.practiceDays, day] }, now);
}

/**
 * Наименьший объём прогона, который может стать рекордом точности.
 *
 * Два символа без промаха — не рекорд, а случайность. Стадия цикла работы над
 * ошибками бывает и в одно слово, и такие прогоны в рекорды не идут.
 */
export const MIN_RECORD_CHARACTERS = 30;

/**
 * Записывает состоявшееся занятие: день практики, рекорды и объём набора.
 *
 * Одна точка на все режимы (ТЗ FR-EN-73): раньше каждый из десяти отмечал
 * день практики сам по себе, и любое новое поле, обновляемое по итогу
 * занятия, пришлось бы дописывать в десять мест — то есть однажды забыть в
 * одном (ТЗ FR-GM-08).
 *
 * Рекорды профиля хранятся, а не вычисляются (ТЗ FR-EN-72): истории занятий
 * на клиенте нет, и восстановить лучший результат было бы не из чего.
 */
export function recordSession(
  workspace: Workspace,
  metrics: TypingMetrics,
  now: string,
): Workspace {
  const withDay = recordPracticeDay(workspace, now, now);
  const profile = activeProfile(withDay);

  // Точность короткого прогона рекордом не считается, а набранные символы
  // считаются: они были набраны.
  const counted = metrics.typedCharacters >= MIN_RECORD_CHARACTERS;

  const updated = withProfile(
    withDay,
    {
      ...profile,
      typedCharacters: profile.typedCharacters + metrics.typedCharacters,
      bestCpm: counted ? Math.max(profile.bestCpm, metrics.cpm) : profile.bestCpm,
      // Рекорд точности — по первому нажатию: текст, доведённый до чистого
      // исправлениями, рекордом набора не является.
      bestAccuracy: counted
        ? Math.max(profile.bestAccuracy, metrics.firstTryAccuracy)
        : profile.bestAccuracy,
    },
    now,
  );

  // Рубежи проверяются здесь же: достижение, за которым надо отдельно
  // сходить, однажды не проверят после нового режима.
  return awardAchievements(withActivity(updated, metrics, now), now);
}

/**
 * Дописывает занятие в журнал по дням.
 *
 * Журнал хранит по дню только сводку — число занятий, число символов и лучшую
 * точность (ТЗ FR-EN-75). Ни нажатий, ни текстов: правило о том, что на
 * устройстве не копится содержание набранного, действует и здесь.
 *
 * Журнал держится в пределах `ACTIVITY_LOG_DAYS`: заданиям хватает недели,
 * а бесконечный список в локальном хранилище однажды предъявят счётом.
 */
function withActivity(workspace: Workspace, metrics: TypingMetrics, now: string): Workspace {
  const day = now.slice(0, 10);
  const existing = workspace.activityLog.find((entry) => entry.date === day);

  const updated: DayActivity = {
    date: day,
    sessions: (existing?.sessions ?? 0) + 1,
    characters: (existing?.characters ?? 0) + metrics.typedCharacters,
    bestAccuracy:
      metrics.typedCharacters >= MIN_RECORD_CHARACTERS
        ? Math.max(existing?.bestAccuracy ?? 0, metrics.firstTryAccuracy)
        : (existing?.bestAccuracy ?? 0),
  };

  const oldest = Date.parse(`${day}T00:00:00.000Z`) - (ACTIVITY_LOG_DAYS - 1) * 86_400_000;
  const log = [...workspace.activityLog.filter((entry) => entry.date !== day), updated]
    .filter((entry) => Date.parse(`${entry.date}T00:00:00.000Z`) >= oldest)
    .sort((left, right) => left.date.localeCompare(right.date));

  return { ...workspace, activityLog: log, updatedAt: now };
}

/**
 * Записывает подтверждённый результат контрольного теста.
 *
 * Подтверждённым считается только тот, который принял сервер: результат,
 * засчитанный самим клиентом, ничего не подтверждает (ТЗ FR-GM-03). Худший
 * результат прежний рекорд не отменяет — рекорд на то и рекорд.
 */
export function recordVerifiedResult(workspace: Workspace, wpm: number, now: string): Workspace {
  const profile = activeProfile(workspace);
  if (wpm <= profile.bestVerifiedWpm) return workspace;

  const updated = withProfile(workspace, { ...profile, bestVerifiedWpm: wpm }, now);
  return awardAchievements(updated, now);
}

/**
 * Записывает снимок доступов, полученный от сервера.
 *
 * Клиент никогда не вычисляет доступы сам: он только хранит то, что прислал
 * сервер (ТЗ FR-SUB-10).
 */
export function setEntitlements(
  workspace: Workspace,
  entitlements: EntitlementSnapshot,
  now: string,
): Workspace {
  return { ...workspace, entitlements, updatedAt: now };
}

/**
 * Действителен ли сохранённый снимок доступов.
 *
 * Офлайн клиент опирается на последний известный ответ сервера, но только
 * ограниченное время (docs/ARCHITECTURE.md §10).
 */
export function hasFreshEntitlements(workspace: Workspace, nowMs: number): boolean {
  if (workspace.entitlements === null) return false;
  const issuedAt = Date.parse(workspace.entitlements.issuedAt);
  if (Number.isNaN(issuedAt)) return false;
  return nowMs - issuedAt < ENTITLEMENT_CACHE_TTL_MS;
}

/** Обновляет настройки приватности. */
export function updatePrivacy(
  workspace: Workspace,
  privacy: Partial<PrivacySettings>,
  now: string,
): Workspace {
  return { ...workspace, privacy: { ...workspace.privacy, ...privacy }, updatedAt: now };
}
